"use client";

import { useEffect, useState } from "react";
import { useRouter } from "@frontend/lib/navigation";
import { Avatar } from "@frontend/components/ui";
import { Modal } from "@frontend/components/modal";
import { clientApi } from "@frontend/lib/api";
import { creatorAvatarUrl } from "@frontend/lib/avatar";
import { summariseDelivery, type ReliabilityTone } from "@frontend/lib/reliability";
import type { Counterpart } from "@creators/backend/lib/collabs/counterparts";

type CounterpartsResponse = {
  recent: Counterpart[];
  match: Counterpart | null;
  canOffer: boolean;
};

/** Long enough that typing a handle is one request, short enough to feel live. */
const DEBOUNCE_MS = 250;

const TONE_TEXT: Record<ReliabilityTone, string> = {
  green: "text-money-700",
  amber: "text-warn-700",
  red: "text-red-700",
  neutral: "text-ink-subtle",
};

export function NewCollabDialog({ onClose }: { onClose: () => void }) {
  const router = useRouter();
  const [query, setQuery] = useState("");
  // The query each result came from, so "is this stale?" is derived rather than
  // tracked — a separate loading flag would have to be set from inside the effect.
  const [result, setResult] = useState<{ query: string; data: CounterpartsResponse } | null>(null);

  const trimmed = query.trim();
  const searching = trimmed !== "" && result?.query !== trimmed;

  useEffect(() => {
    const controller = new AbortController();
    // Only a typed query is worth debouncing; the first load should feel instant.
    const delay = trimmed ? DEBOUNCE_MS : 0;

    const timer = window.setTimeout(async () => {
      try {
        const path = trimmed
          ? `/api/counterparts?q=${encodeURIComponent(trimmed)}`
          : "/api/counterparts";
        const data = await clientApi<CounterpartsResponse>(path, { signal: controller.signal });
        setResult({ query: trimmed, data });
      } catch {
        // An aborted request has been superseded by a newer one, so leave the
        // last good result on screen rather than flashing between keystrokes.
      }
    }, delay);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [trimmed]);

  const open = (counterpart: Counterpart) => {
    onClose();
    router.push(`/${counterpart.handle}`);
  };

  return (
    <Modal
      title="New collab"
      description="Pick someone you have worked with, or find them by their SendCollab username."
      onClose={onClose}
      size="lg"
    >
      <div className="flex flex-col gap-4">
        <div className="relative shrink-0">
          <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-subtle">
            <SearchIcon />
          </span>
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="@username"
            autoComplete="off"
            spellCheck={false}
            aria-label="Find a creator by SendCollab username"
            className="h-11 w-full rounded-xl border border-line bg-surface py-0 pr-3 pl-9 text-sm leading-normal text-ink outline-none transition placeholder:text-ink-subtle focus:border-brand-500 focus:ring-4 focus:ring-brand-100"
          />
        </div>

        {/* Fixed panel so empty / loading / results never resize the dialog. */}
        <div className="relative h-[min(20rem,50dvh)] overflow-hidden rounded-xl border border-line bg-canvas/40">
          <div
            className={`h-full overflow-y-auto overscroll-contain p-2 transition-opacity ${
              searching ? "opacity-55" : "opacity-100"
            }`}
          >
            {/* Drive list from the last settled query so typing never flashes a false miss. */}
            <Results
              data={result?.data ?? null}
              query={result?.query ?? ""}
              onSelect={open}
            />
          </div>
          {searching ? (
            <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center pt-2.5">
              <span className="rounded-full border border-line bg-surface px-2.5 py-0.5 text-[11px] font-medium text-ink-muted shadow-sm">
                Searching…
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

function Results({
  data,
  query,
  onSelect,
}: {
  data: CounterpartsResponse | null;
  query: string;
  onSelect: (counterpart: Counterpart) => void;
}) {
  if (!data) {
    return (
      <div className="space-y-2 p-1" aria-busy="true" aria-label="Loading">
        <div className="h-14 animate-pulse rounded-lg bg-ink/[0.06]" />
        <div className="h-14 animate-pulse rounded-lg bg-ink/[0.06]" />
        <div className="h-14 animate-pulse rounded-lg bg-ink/[0.06]" />
      </div>
    );
  }

  if (!data.canOffer) {
    return (
      <EmptyState>
        This account cannot send offers yet. Sign in with an account that can book creators.
      </EmptyState>
    );
  }

  if (query) {
    if (!data.match) {
      return (
        <EmptyState>
          No SendCollab user matches “{query}”. Check the spelling, or ask them for their @username.
        </EmptyState>
      );
    }

    return (
      <ul className="space-y-0.5">
        <CounterpartRow counterpart={data.match} onSelect={onSelect} />
      </ul>
    );
  }

  if (data.recent.length === 0) {
    return (
      <EmptyState>
        You have not booked anyone yet. Search for a creator by their @username on SendCollab.
      </EmptyState>
    );
  }

  return (
    <>
      <h4 className="px-2 pt-1 pb-1.5 text-[11px] font-semibold tracking-wide text-ink-subtle uppercase">
        Worked with before
      </h4>
      <ul className="space-y-0.5">
        {data.recent.map((counterpart) => (
          <CounterpartRow
            key={counterpart.creatorId}
            counterpart={counterpart}
            onSelect={onSelect}
          />
        ))}
      </ul>
    </>
  );
}

function CounterpartRow({
  counterpart,
  onSelect,
}: {
  counterpart: Counterpart;
  onSelect: (counterpart: Counterpart) => void;
}) {
  const delivery = summariseDelivery(counterpart.delivery);

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(counterpart)}
        className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-surface focus-visible:bg-surface focus-visible:outline-none"
      >
        <Avatar
          name={counterpart.displayName}
          url={creatorAvatarUrl(counterpart.creatorId, counterpart.avatarKey)}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink">
            {counterpart.displayName}
          </span>
          <span className="block truncate text-xs text-ink-subtle">@{counterpart.handle}</span>
        </span>
        <span className="shrink-0 text-right">
          {delivery.value ? (
            <span className={`tabular block text-sm font-semibold ${TONE_TEXT[delivery.tone]}`}>
              {delivery.value}
            </span>
          ) : null}
          <span className="block text-[11px] text-ink-subtle">{delivery.detail}</span>
        </span>
      </button>
    </li>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-[16rem] items-center justify-center px-4 py-8">
      <p className="max-w-sm text-center text-sm leading-relaxed text-ink-muted">{children}</p>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-4" aria-hidden="true">
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="m10.5 10.5 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
